import { DatasetCreateRequest } from '@/api/generated/models'

/**
 * FORM VALIDATION UTILITIES
 *
 * This module handles validation logic for the Dataset Form's multiple use cases.
 * Each use case has different required fields and validation rules.
 *
 * USE CASES:
 * 1. Contextual Dataset (External driver)
 * 2. Client Dataset (Internal driver)
 * 3. Media Channel Dataset (Internal driver + media channel flag)
 * 4. Regional Dataset (Segment)
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type ValidationError = {
  field: keyof DatasetCreateRequest | 'region_id' | 'parent_dataset_id'
  message: string
}

export type FormMode = 'contextual' | 'client' | 'media_channel' | 'segment'

export interface ValidationContext {
  isSegment: boolean
  isContextual: boolean
  partOfMediaChannel: boolean
  isEdit: boolean
}

// ============================================================================
// VALIDATION RULE DEFINITIONS
// ============================================================================

/**
 * Fields required for ALL non-segment datasets
 */
const BASE_REQUIRED_FIELDS = {
  name: 'Driver name is required',
  source: 'Source is required',
  interval: 'Frequency is required',
  units: 'Measure is required',
} as const

/**
 * Fields required for segment creation
 */
const SEGMENT_REQUIRED_FIELDS = {
  name: 'Driver name is required',
} as const

/**
 * Additional fields required for client datasets
 */
const CLIENT_REQUIRED_FIELDS = {
  client_id: 'Client is required',
} as const

/**
 * Additional fields required for media channel datasets
 */
const MEDIA_CHANNEL_REQUIRED_FIELDS = {
  category: 'Category must be selected',
  'media_parameters.input_category': 'Exposure category is required',
} as const

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Determines the current form mode based on state
 */
export function getFormMode(context: ValidationContext): FormMode {
  if (context.isSegment) return 'segment'
  if (context.partOfMediaChannel) return 'media_channel'
  if (!context.isContextual) return 'client'
  return 'contextual'
}

/**
 * Validates that at least one market is selected
 */
function validateMarkets(
  data: Partial<DatasetCreateRequest>,
): ValidationError | null {
  if (!data.market_ids || data.market_ids.length === 0) {
    return {
      field: 'market_ids',
      message: 'At least one country must be selected',
    }
  }
  return null
}

/**
 * Validates file upload based on context
 */
function validateFile(
  fileInfo: any,
  context: ValidationContext,
): ValidationError | null {
  // File is required for segments in create mode
  if (context.isSegment && !context.isEdit && !fileInfo) {
    return {
      field: 'file',
      message: 'File is required for segments',
    }
  }

  // File is optional for other dataset types
  return null
}

/**
 * Validates parent dataset selection for segments
 */
function validateParentDataset(
  selectedParentDataset: any[],
  context: ValidationContext,
): ValidationError | null {
  if (
    context.isSegment &&
    (!selectedParentDataset || selectedParentDataset.length === 0)
  ) {
    return {
      field: 'parent_dataset_id',
      message: 'Parent driver is required',
    }
  }
  return null
}

/**
 * Validates region selection for segments
 */
function validateRegion(
  selectedRegions: any[],
  context: ValidationContext,
): ValidationError | null {
  if (context.isSegment && (!selectedRegions || selectedRegions.length === 0)) {
    return {
      field: 'region_id',
      message: 'Segment name is required',
    }
  }
  return null
}

/**
 * Validates required fields based on form mode
 */
function validateRequiredFields(
  data: Partial<DatasetCreateRequest>,
  mode: FormMode,
): ValidationError[] {
  const errors: ValidationError[] = []

  // Get required fields for this mode
  let requiredFields: Record<string, string> = {}

  switch (mode) {
    case 'segment':
      requiredFields = { ...SEGMENT_REQUIRED_FIELDS }
      break

    case 'contextual':
      requiredFields = { ...BASE_REQUIRED_FIELDS }
      break

    case 'client':
      requiredFields = {
        ...BASE_REQUIRED_FIELDS,
        ...CLIENT_REQUIRED_FIELDS,
      }
      break

    case 'media_channel':
      requiredFields = {
        ...BASE_REQUIRED_FIELDS,
        ...CLIENT_REQUIRED_FIELDS,
        ...MEDIA_CHANNEL_REQUIRED_FIELDS,
      }
      break
  }

  // Check each required field
  Object.entries(requiredFields).forEach(([field, message]) => {
    // Handle nested fields (e.g., 'media_parameters.input_category')
    if (field.includes('.')) {
      const [parent, child] = field.split('.')
      const parentValue = (data as any)[parent]
      if (!parentValue || !parentValue[child]) {
        errors.push({ field: field as any, message })
      }
    } else {
      const value = (data as any)[field]
      if (value === undefined || value === null || value === '') {
        errors.push({ field: field as any, message })
      }
    }
  })

  return errors
}

/**
 * Validates media parameters based on input category
 */
function validateMediaParameters(
  data: Partial<DatasetCreateRequest>,
): ValidationError[] {
  const errors: ValidationError[] = []

  // Skip if not a media channel dataset
  if (!data.part_of_media_channel) return errors

  const mediaParams = data.media_parameters

  // Input category is required (already checked in required fields)
  if (!mediaParams?.input_category) return errors

  // Constraints and ROI are only allowed for 'spend' input category
  // This is enforced on the backend, but we can provide better UX by validating here
  if (mediaParams.input_category !== 'spend') {
    // Clear these values if they exist (they'll be ignored by backend anyway)
    // This is more of a data cleaning step than validation
  }

  return errors
}

// ============================================================================
// MAIN VALIDATION FUNCTION
// ============================================================================

/**
 * Validates the entire form based on current context
 *
 * @param data - Form data to validate
 * @param fileInfo - File upload info (null if no file)
 * @param selectedParentDataset - Selected parent dataset (for segments)
 * @param selectedRegions - Selected regions (for segments)
 * @param context - Validation context (form state)
 * @returns Array of validation errors (empty if valid)
 *
 * @example
 * // Validate segment creation
 * const errors = validateDatasetForm(
 *   { name: "California Sales" },
 *   fileInfo,
 *   [{ id: "123", name: "National Sales" }],
 *   [{ id: "10", name: "California" }],
 *   { isSegment: true, isContextual: true, partOfMediaChannel: false, isEdit: false }
 * )
 */
export function validateDatasetForm(
  data: Partial<DatasetCreateRequest>,
  fileInfo: any,
  selectedParentDataset: any[],
  selectedRegions: any[],
  context: ValidationContext,
): ValidationError[] {
  const errors: ValidationError[] = []
  const mode = getFormMode(context)

  // 1. Validate required fields based on mode
  errors.push(...validateRequiredFields(data, mode))

  // 2. Mode-specific validations
  if (mode === 'segment') {
    // Segment-specific validation
    const parentError = validateParentDataset(selectedParentDataset, context)
    if (parentError) errors.push(parentError)

    const fileError = validateFile(fileInfo, context)
    if (fileError) errors.push(fileError)

    const regionError = validateRegion(selectedRegions, context)
    if (regionError) errors.push(regionError)
  } else {
    // Regular dataset validation
    const marketError = validateMarkets(data)
    if (marketError) errors.push(marketError)

    // Media channel specific validation
    if (mode === 'media_channel') {
      errors.push(...validateMediaParameters(data))
    }
  }

  return errors
}

/**
 * Converts validation errors to react-hook-form format
 */
export function errorsToFormErrors(
  errors: ValidationError[],
): Record<string, { type: string; message: string }> {
  const formErrors: Record<string, { type: string; message: string }> = {}

  errors.forEach((error) => {
    formErrors[error.field] = {
      type: 'validation',
      message: error.message,
    }
  })

  return formErrors
}

// ============================================================================
// SERVER ERROR HANDLING
// ============================================================================

/**
 * Flattens a nested error object into a single level.
 * e.g., { media_parameters: { input_category: "error" } } becomes { "media_parameters.input_category": "error" }
 */
export function flattenErrorObject(
  obj: any,
  prefix = '',
): Record<string, string> {
  return Object.keys(obj).reduce(
    (acc, k) => {
      const pre = prefix.length ? prefix + '.' : ''
      if (
        typeof obj[k] === 'object' &&
        obj[k] !== null &&
        !Array.isArray(obj[k])
      ) {
        Object.assign(acc, flattenErrorObject(obj[k], pre + k))
      } else {
        // Handle both string errors and array errors (Django often returns arrays)
        const errorValue = Array.isArray(obj[k]) ? obj[k][0] : obj[k]
        acc[pre + k] = String(errorValue)
      }
      return acc
    },
    {} as Record<string, string>,
  )
}

/**
 * Maps backend API field names to frontend form field names
 */
export function findFormFieldForApiKey(
  apiKey: string,
  allFieldNames: (keyof DatasetCreateRequest)[],
): keyof DatasetCreateRequest | string | null {
  // 1. Direct mappings for known API -> Form field differences
  const fieldMappings: Record<string, string> = {
    client: 'client_id',
    markets: 'market_ids',
    aggregation_method: 'aggregation_method', // Already matches
    // Nested media parameters are flattened in the form
    'media_parameters.input_category': 'media_parameters.input_category',
    'media_parameters.channel_lag': 'media_parameters.channel_lag',
    'media_parameters.spend_constraint': 'media_parameters.spend_constraint',
    'media_parameters.roi_assumption': 'media_parameters.roi_assumption',
  }

  // Check for direct mapping first
  if (fieldMappings[apiKey]) {
    return fieldMappings[apiKey]
  }

  // 2. Try to find exact match in form fields
  if (allFieldNames.includes(apiKey as any)) {
    return apiKey
  }

  // 3. Find the longest form field name that matches part of the API key
  const bestMatch = allFieldNames.reduce(
    (currentBest, fieldName) => {
      const fieldStr = String(fieldName)
      if (apiKey.includes(fieldStr)) {
        if (!currentBest || fieldStr.length > currentBest.length) {
          return fieldName
        }
      }
      return currentBest
    },
    null as keyof DatasetCreateRequest | null,
  )

  return bestMatch
}

/**
 * Handles server errors and maps them to form fields
 *
 * @param errorData - Error response from server (can be string, object, or array)
 * @param formFieldNames - List of all form field names
 * @returns Object with field errors and unmapped errors
 */
export function handleServerErrors(
  errorData: any,
  formFieldNames: (keyof DatasetCreateRequest)[],
): {
  fieldErrors: Record<string, { type: string; message: string }>
  unmappedErrors: string[]
} {
  const fieldErrors: Record<string, { type: string; message: string }> = {}
  const unmappedErrors: string[] = []

  // Parse if string
  let errorObject = errorData
  if (typeof errorObject === 'string') {
    try {
      errorObject = JSON.parse(errorObject)
    } catch (e) {
      // If it's not JSON, treat as a general error message
      return { fieldErrors, unmappedErrors: [errorObject] }
    }
  }

  // If still a string after parsing attempt, it's a general error
  if (typeof errorObject === 'string') {
    return { fieldErrors, unmappedErrors: [errorObject] }
  }

  // If not an object, return general error
  if (typeof errorObject !== 'object' || errorObject === null) {
    return { fieldErrors, unmappedErrors: ['An unexpected error occurred.'] }
  }

  // Flatten nested errors
  const flattenedErrors = flattenErrorObject(errorObject)

  // Map to form fields
  Object.entries(flattenedErrors).forEach(([key, message]) => {
    const fieldName = findFormFieldForApiKey(key, formFieldNames)

    if (fieldName && typeof message === 'string') {
      fieldErrors[fieldName] = {
        type: 'server',
        message: message,
      }
    } else {
      // Couldn't map to a form field, add to unmapped errors
      unmappedErrors.push(
        typeof message === 'string' ? message : JSON.stringify(message),
      )
    }
  })

  return { fieldErrors, unmappedErrors }
}
